// src/features/dzban/pages/DzbanRanking.jsx
// DARK THEME VERSION - "Hall of Shame"
// Ranking page - TOP 5 Dzbanów Roku (Hybrid Podium)
// SEO OPTIMIZED - MAY 2026

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Trophy, ArrowLeft, Share2, MapPin, Car, Flame } from 'lucide-react';
import { useDzbanVoting } from '../hooks/useDzbanVoting';

export const DzbanRanking = () => {
  const navigate = useNavigate();
  const { ranking, fetchRanking } = useDzbanVoting();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRanking = async () => {
      setLoading(true);
      await fetchRanking();
      setLoading(false);
    };
    loadRanking();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const top5 = ranking.slice(0, 5);

  const handleShare = (spot, position) => {
    const medals = { 1: '🥇', 2: '🥈', 3: '🥉' };
    const medal = medals[position] || `#${position}`;
    const text = `${medal} Chamstwo Drogowe - Dzban Roku 2026: ${spot.brand || 'Auto'} ${spot.plate}\n${spot.votes} głosów przeciwko! Najgorsze parkowanie Polski.\n\nSprawdź TOP 5: ${window.location.origin}/dzban-roku/ranking`;
    
    if (navigator.share) {
      navigator.share({ title: 'Dzban Roku 2026 - TOP 5', text });
    } else {
      navigator.clipboard.writeText(text);
      alert('Link skopiowany do schowka!');
    }
  };

  const PodiumCard = ({ spot, position }) => {
    const imageUrl = spot.images?.[0] || spot.imageUrl || 'https://via.placeholder.com/400x300';
    
    const borderColor = {
      1: '#ffd700', // gold
      2: '#c0c0c0', // silver
      3: '#cd7f32'  // bronze
    }[position];

    const glowColor = {
      1: '0 0 40px rgba(255,215,0,0.6), 0 8px 32px rgba(0,0,0,0.5)',
      2: '0 0 30px rgba(192,192,192,0.5), 0 8px 32px rgba(0,0,0,0.5)',
      3: '0 0 30px rgba(205,127,50,0.5), 0 8px 32px rgba(0,0,0,0.5)'
    }[position];

    const borderWidth = position === 1 ? '6px' : '4px';
    const scaleClass = position === 1 ? 'lg:scale-105' : '';

    return (
      <div className={`flex-1 ${scaleClass} transition-transform duration-300`}>
        <div 
          className="rounded-xl lg:rounded-2xl overflow-hidden border border-gray-800 hover:border-gray-700 transition-all duration-300 group cursor-pointer"
          style={{
            backgroundColor: '#1f1f1f',
            borderBottom: `${borderWidth} solid ${borderColor}`,
            boxShadow: glowColor
          }}
          onClick={() => navigate(`/pojazd/${spot.id}`)}
          role="article"
          aria-label={`${position} miejsce: ${spot.brand} ${spot.plate} - ${spot.votes} głosów`}
        >
          {/* Photo */}
          <div className="relative aspect-[4/3] lg:aspect-video bg-gray-900 overflow-hidden">
            <img 
              src={imageUrl}
              alt={`Chamstwo drogowe ${spot.brand} ${spot.plate} - miejsce ${position} w rankingu najgorszego parkowania`}
              className="w-full h-full object-cover transition-all duration-400 group-hover:scale-105"
              style={{ filter: 'contrast(1.25) saturate(1.1) grayscale(30%)' }}
              onMouseEnter={(e) => e.currentTarget.style.filter = 'contrast(1.3) saturate(1.3) grayscale(0%)'}
              onMouseLeave={(e) => e.currentTarget.style.filter = 'contrast(1.25) saturate(1.1) grayscale(30%)'}
            />
            
            {/* Position badge */}
            <div 
              className="absolute top-3 right-3 w-12 h-12 lg:w-16 lg:h-16 rounded-full flex items-center justify-center text-white font-black text-2xl lg:text-3xl border-4 border-black"
              style={{ 
                background: `linear-gradient(135deg, ${borderColor}, ${borderColor})`,
                boxShadow: `0 0 20px rgba(${position === 1 ? '255,215,0' : position === 2 ? '192,192,192' : '205,127,50'},0.8)`,
                fontFamily: '"Bebas Neue", "Impact", sans-serif'
              }}
              aria-label={`Miejsce ${position}`}
            >
              {position}
            </div>
          </div>

          {/* Info */}
          <div className="p-4 lg:p-5 bg-[#0a0a0a]/40 backdrop-blur-sm border-t border-gray-800">
            <div className="text-center">
              {/* Plate */}
              <div 
                className="text-xl lg:text-2xl font-black text-white mb-1 uppercase tracking-wide"
                style={{ 
                  textShadow: '0 0 10px rgba(255, 255, 255, 0.2)',
                  fontFamily: '"Bebas Neue", "Impact", sans-serif'
                }}
              >
                {spot.plate || 'BRAK'}
              </div>
              
              {/* Brand */}
              <div className="text-xs lg:text-sm text-gray-400 uppercase tracking-wider mb-2">
                {spot.brand || 'Nieznana marka'}
              </div>
              
              {/* Location */}
              {spot.locationName && (
                <div className="text-[10px] lg:text-xs text-orange-400 mb-3 font-semibold">
                  📍 {spot.locationName}
                </div>
              )}

              {/* Votes */}
              <div className="flex items-center justify-center gap-2">
                <div 
                  className="text-3xl lg:text-4xl font-black"
                  style={{ 
                    color: borderColor,
                    textShadow: `0 0 15px rgba(${position === 1 ? '255,215,0' : position === 2 ? '192,192,192' : '205,127,50'},0.6)`,
                    fontFamily: '"Orbitron", monospace'
                  }}
                >
                  {spot.votes}
                </div>
                <Flame size={24} className="text-orange-500" aria-hidden="true" />
              </div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">głosów</div>

              {/* Share button (only #1) */}
              {position === 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleShare(spot, position);
                  }}
                  className="mt-3 px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-all flex items-center gap-2 mx-auto"
                  aria-label="Udostępnij zwycięzcę Dzban Roku 2026"
                >
                  <Share2 size={14} aria-hidden="true" />
                  UDOSTĘPNIJ
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        {/* SEO OPTIMIZED - TARGET: "ranking chamstwa drogowego" */}
        <title>TOP 5 Chamstwo Drogowe Polski 2026 | Ranking Najgorszego Parkowania - Dzban Roku</title>
        <meta name="description" content={`Odkryj TOP 5 najgorszego chamstwa drogowego w Polsce! Złote, srebrne i brązowe podium najgorszego parkowania - społeczny ranking Dzban Roku 2026. ${top5.length > 0 ? `#1: ${top5[0]?.brand} ${top5[0]?.plate} z ${top5[0]?.votes} głosami. ` : ''}Hall of Shame kierowców - mistrz parkowania, bloker, agresor. Zagłosuj teraz!`} />
        <meta name="keywords" content="chamstwo drogowe ranking, top 5 najgorsi kierowcy, ranking parkowanie polska, dzban roku 2026, najgorsze parkowanie ranking, hall of shame kierowcy, mistrz parkowania ranking, złe parkowanie top 5, najgorsi kierowcy polski 2026, chamstwo drogowe społeczność" />
        
        {/* Dynamic OG based on #1 winner */}
        {top5[0] && (
          <>
            <meta property="og:title" content={`🥇 Dzban Roku 2026: ${top5[0].brand} ${top5[0].plate} - ${top5[0].votes} głosów!`} />
            <meta property="og:description" content={`${top5[0].brand} ${top5[0].plate} wygrywa Hall of Shame jako najgorsze chamstwo drogowe 2026! Zobacz pełny TOP 5 rankingu najgorszego parkowania w Polsce.`} />
            <meta property="og:image" content={top5[0].images?.[0] || top5[0].imageUrl || 'https://motoradar24.pl/og-image.jpg'} />
          </>
        )}
        {!top5[0] && (
          <>
            <meta property="og:title" content="TOP 5 Chamstwo Drogowe - Dzban Roku 2026" />
            <meta property="og:description" content="Ranking najgorszego parkowania w Polsce! Zagłosuj na najgorsze chamstwo drogowe i zdobądź Hall of Shame 2026!" />
            <meta property="og:image" content="https://motoradar24.pl/og-image.jpg" />
          </>
        )}
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://motoradar24.pl/dzban-roku/ranking" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TOP 5 Chamstwo Drogowe 2026 - Hall of Shame" />
        
        {/* Canonical + Robots */}
        <link rel="canonical" href="https://motoradar24.pl/dzban-roku/ranking" />
        <meta name="robots" content="index, follow, max-image-preview:large" />
      </Helmet>

      {/* DARK BACKGROUND */}
      <div className="min-h-screen pb-32 lg:pb-4 relative overflow-hidden" style={{ backgroundColor: '#0a0a0a' }}>
        {/* Background texture overlay */}
        <div 
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.02) 10px, rgba(255,255,255,.02) 20px)',
          }}
        />

        {/* HEADER - Dark */}
        <div className="relative bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a] text-white py-10 px-4 border-b border-gray-800">
          <div className="max-w-6xl mx-auto">
            {/* Back button */}
            <button
              onClick={() => navigate('/dzban-roku')}
              className="mb-6 flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              aria-label="Wróć do głosowania Dzban Roku"
            >
              <ArrowLeft size={20} />
              <span className="text-sm font-semibold">WRÓĆ DO GŁOSOWANIA</span>
            </button>

            <div className="text-center relative z-10">
              {/* H1 with keywords */}
              <div className="flex items-center justify-center gap-3 mb-4">
                <Trophy 
                  size={40} 
                  className="text-amber-500 animate-pulse"
                  style={{ filter: 'drop-shadow(0 0 10px rgba(251, 191, 36, 0.5))' }}
                  aria-hidden="true"
                />
                <h1 
                  className="text-5xl font-black uppercase tracking-tight"
                  style={{ 
                    fontFamily: '"Bebas Neue", "Impact", sans-serif',
                    textShadow: '0 0 20px rgba(255, 255, 255, 0.2)',
                    letterSpacing: '3px'
                  }}
                >
                  HALL OF{' '}
                  <span className="bg-gradient-to-r from-amber-500 to-yellow-600 bg-clip-text text-transparent">
                    SHAME
                  </span>
                </h1>
                <Trophy 
                  size={40} 
                  className="text-amber-500 animate-pulse"
                  style={{ filter: 'drop-shadow(0 0 10px rgba(251, 191, 36, 0.5))' }}
                  aria-hidden="true"
                />
              </div>
              
              {/* Subtitle with keywords */}
              <p className="text-lg text-gray-400 italic">
                TOP 5 Najgorszego Chamstwa Drogowego Polski - Dzban Roku 2026
              </p>
            </div>
          </div>
        </div>

        {/* CONTENT */}
        <div className="max-w-6xl mx-auto p-4 py-8 relative z-10">
          {loading ? (
            <div className="text-center py-20">
              <div 
                className="animate-spin rounded-full h-16 w-16 border-4 border-amber-500 border-t-transparent mx-auto mb-4"
                style={{ boxShadow: '0 0 20px rgba(251, 191, 36, 0.4)' }}
                role="status"
                aria-label="Ładowanie rankingu"
              />
              <p className="text-gray-400 font-semibold">Ładowanie rankingu chamstwa drogowego...</p>
            </div>
          ) : top5.length === 0 ? (
            <div 
              className="text-center py-20 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10"
              style={{ boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)' }}
            >
              <Trophy size={64} className="mx-auto mb-4 text-gray-600" aria-hidden="true" />
              <p className="text-gray-400 font-semibold mb-2 text-xl">
                Ranking chamstwa drogowego jest pusty
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Zagłosuj na pierwsze spoty żeby rozpocząć ranking najgorszego parkowania!
              </p>
              <button
                onClick={() => navigate('/dzban-roku')}
                className="px-8 py-4 bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold rounded-xl hover:from-red-700 hover:to-orange-700 transition-all"
                aria-label="Zacznij głosować na chamstwo drogowe"
              >
                Zacznij głosować
              </button>
            </div>
          ) : (
            <>
              {/* PODIUM - TOP 3 (Hybrid: 3-col desktop, stack mobile) */}
              {top5.length >= 3 && (
                <div className="mb-12">
                  <h2 className="text-2xl font-black text-center mb-8 text-white uppercase tracking-wide">
                    🏆 Podium Chamstwa Drogowego 🏆
                  </h2>
                  
                  {/* Desktop: 3-column podium */}
                  <div className="hidden lg:flex gap-6 items-end justify-center mb-8">
                    {/* #2 Silver */}
                    <PodiumCard spot={top5[1]} position={2} />
                    
                    {/* #1 Gold (tallest) */}
                    <PodiumCard spot={top5[0]} position={1} />
                    
                    {/* #3 Bronze */}
                    <PodiumCard spot={top5[2]} position={3} />
                  </div>

                  {/* Mobile: Stacked */}
                  <div className="lg:hidden space-y-4 mb-8">
                    <PodiumCard spot={top5[0]} position={1} />
                    <PodiumCard spot={top5[1]} position={2} />
                    <PodiumCard spot={top5[2]} position={3} />
                  </div>
                </div>
              )}

              {/* #4 & #5 - Regular cards */}
              {top5.length > 3 && (
                <div className="space-y-4 mb-8">
                  <h3 className="text-xl font-black text-center mb-6 text-gray-400 uppercase tracking-wide">
                    Pozostali finaliści Hall of Shame
                  </h3>
                  
                  {top5.slice(3, 5).map((spot, index) => {
                    const position = index + 4;
                    const imageUrl = spot.images?.[0] || spot.imageUrl || 'https://via.placeholder.com/400x300';
                    
                    return (
                      <div 
                        key={spot.id}
                        className="rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all cursor-pointer group"
                        style={{
                          backgroundColor: '#1f1f1f',
                          borderBottom: '3px solid #ff6600',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
                        }}
                        onClick={() => navigate(`/pojazd/${spot.id}`)}
                        role="article"
                        aria-label={`${position} miejsce: ${spot.brand} ${spot.plate} - ${spot.votes} głosów`}
                      >
                        <div className="flex items-center gap-4 p-4">
                          {/* Position */}
                          <div 
                            className="text-4xl font-black text-gray-600 w-12 text-center"
                            style={{ fontFamily: '"Bebas Neue", sans-serif' }}
                          >
                            {position}
                          </div>

                          {/* Image */}
                          <div className="w-20 h-20 bg-gray-900 rounded-lg overflow-hidden flex-shrink-0">
                            <img 
                              src={imageUrl} 
                              alt={`Chamstwo drogowe ${spot.brand} ${spot.plate} - miejsce ${position}`}
                              className="w-full h-full object-cover transition-all duration-300 group-hover:scale-110"
                              style={{ filter: 'grayscale(30%)' }}
                            />
                          </div>

                          {/* Info */}
                          <div className="flex-1">
                            <div 
                              className="text-xl font-black text-white"
                              style={{ fontFamily: '"Bebas Neue", sans-serif' }}
                            >
                              {spot.plate || 'BRAK TABLICY'}
                            </div>
                            <div className="text-sm text-gray-400">
                              {spot.brand || 'Nieznana marka'}
                            </div>
                          </div>

                          {/* Votes */}
                          <div className="text-right">
                            <div 
                              className="text-2xl font-black text-orange-500"
                              style={{ fontFamily: '"Orbitron", monospace' }}
                            >
                              {spot.votes}
                            </div>
                            <div className="text-xs text-gray-500">
                              głosów
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Navigation buttons */}
              <div className="flex gap-4 justify-center flex-wrap">
                <button
                  onClick={() => navigate('/dzban-roku')}
                  className="px-8 py-4 bg-gradient-to-r from-red-600 to-orange-600 text-white font-bold rounded-xl hover:from-red-700 hover:to-orange-700 transition-all"
                  aria-label="Zagłosuj na więcej chamstwa drogowego"
                >
                  Zagłosuj na więcej!
                </button>
                
                <button
                  onClick={() => navigate('/dzban-roku/miasta')}
                  className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all flex items-center gap-2"
                  aria-label="Zobacz ranking miast - chamstwo drogowe"
                >
                  <MapPin size={20} aria-hidden="true" />
                  Ranking Miast
                </button>
                
                <button
                  onClick={() => navigate('/dzban-roku/marki')}
                  className="px-8 py-4 bg-gradient-to-r from-slate-600 to-slate-700 text-white font-bold rounded-xl hover:from-slate-700 hover:to-gray-700 transition-all flex items-center gap-2"
                  aria-label="Zobacz ranking marek - najgorsze parkowanie"
                >
                  <Car size={20} aria-hidden="true" />
                  Ranking Marek
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};